import {
  Document,
  DocumentMetadata,
  Edge,
  EdgeMetadata,
  ObjectWithId,
  ObjectWithKey,
} from 'arangojs/documents';
import { number, string } from 'yup';
import {
  ACCESS_LEVELS,
  ActivatingEventValidator,
  DayRemindersValidator,
  LocationValidator,
  PrivacySettings,
  ThoughtValidator,
} from '.';
import { featureFlags } from './features';
import {
  DateTimeReminderValidator,
  PracticeLocationValidator,
  PracticeValidator,
  ProfileValidator,
  TestValidator,
} from './validators';

export const dbCols = {
  activatingEvents: 'activatingEvents',
  abcModels: 'abcModels',
  appConfig: 'appConfig',
  behaviorExperiments: 'behaviorExperiments',
  conceptualizations: 'conceptualizations',
  dateTimeReminders: 'dateTimeReminders',
  dayReminders: 'dayReminders',
  downwardArrows: 'downwardArrows',
  exposures: 'exposures',
  exposureStimuli: 'exposureStimuli',
  featureFlags: 'featureFlags',
  invites: 'invites',
  locations: 'locations',
  locationsRelations: 'locationsRelations',
  messages: 'messages',
  migration_history: 'migration_history',
  practices: 'practices',
  practicesLocations: 'practicesLocations',
  practiceUsageEventsRelations: 'practiceUsageEventsRelations',
  progressNotes: 'progressNotes',
  sessionsRelations: 'sessionsRelations',
  subscriptions: 'subscriptions',
  supportingCycles: 'supportingCycles',
  tests: 'tests',
  therapistClientRelations: 'therapistClientRelations',
  therapistProfiles: 'therapistProfiles',
  thoughts: 'thoughts',
  usageEventsRelations: 'usageEventsRelations',
  users: 'users',
  userSyncState: 'user_sync_state',
  worryTrees: 'worryTrees',
} as const;

export type ArangoObjectSelector = ObjectWithId | ObjectWithKey;

// Singleton config collection (E27). One doc per surface; the mobile version gate
// reads _key === 'mobile'. Updatable without a deploy (that is the point).
export type AppConfig = DocumentMetadata & {
  minSupportedVersion?: string | null;
  minSupportedVersionIos?: string | null;
  minSupportedVersionAndroid?: string | null;
  updatedAt?: string | null;
};

export type Created = {
  createdAt: string;
};
export type CreatedUpdated = Created & {
  updatedAt: string;
};
export type UserId = {
  userId: string;
};

export enum UserRole {
  FREE,
  PAID,
  THERAPIST,
  THERAPIST_ADMIN,
  ADMIN,
  NON_MOBILE,
}

export type DataWithCursor<T extends {}> = {
  data: Document<T>[];
  cursor?: number;
};

export type RelayPagination<T extends {}> = {
  totalCount: number;
  edges: {
    cursor: string;
    node: Document<T>;
  }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export type RelayPaginationEdge<T extends {}> = {
  totalCount: number;
  edges: {
    cursor: string;
    node: Edge<T>;
  }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

// TODO: move to validators or only GraphQL
export type User = DocumentMetadata &
  CreatedUpdated & {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    timezone: string;
    role: UserRole;
    lng: string;
    providerId: string;
    photoUrl?: string | null;
    uid: string;
    fcmToken?: string | null;
    accessLevel?: keyof typeof ACCESS_LEVELS | null;
    adaptyProfileId?: string | null;
    hasAdaptyPremiumAccess?: boolean | null;
    featureFlags?: typeof featureFlags;
    privacySettings?: PrivacySettings | null;
    phone?: string | null;
    extension?: string | null;
    country?: string | null;
    state?: string | null;
    practiceId?: string | null;
    subscriptionExpiredAt?: string | null;
    stripeCustomerId?: string | null;
    practice?: Practice | null;
    diagnoses?: Array<string | null> | null;
    gender?: number | null;
    preferredPronouns?: number | null;
    needsNotificationsSync?: boolean | null;
    // Per-user revocation counter for CC-issued auth tokens (E26). Missing = 0.
    // Incrementing it (revokeAuthTokens) invalidates every previously minted
    // access/refresh token whose `ver` claim no longer matches.
    authTokenVersion?: number | null;
  };

export type TherapistDataAccess = {
  allowView?: boolean;
  allowEdit?: boolean;
  allowAdminView?: boolean;
  allowAdminEdit?: boolean;
};
export type Client = User & {
  mostUsedStats?: {
    entityId: string;
    count: number;
  };
  tests?: Test[];
  access?: TherapistDataAccess;
  thoughts?: Thought[];
  enabledDayRemindersCount?: number;
  enabledLocationRemindersCount?: number;
  cardsViewedStats?: UsageEventStats;
  crisisStats?: UsageEventStats;
  dayRemindersTriggeredStats?: UsageEventStats;
  dayRemindersReactedStats?: UsageEventStats;
  locationRemindersTriggeredStats?: UsageEventStats;
  locationRemindersReactedStats?: UsageEventStats;
  meditationsStats?: UsageEventStats;
  activeSubscriptions?: {
    data: Subscription[];
    count: number;
  };
  /**
   * F-203 (founder Q-f196-2 B, 2026-09-30): may the caller see this client's own work — the view
   * question the own-work fields above ask (F-196 / F-202). The client page shows "The client doesn't
   * share this with you." when it is false.
   */
  workShared?: boolean | null;
};

export type ActivatingEvent = DocumentMetadata &
  UserId &
  CreatedUpdated &
  ActivatingEventValidator;

export type DayReminders = DocumentMetadata &
  CreatedUpdated &
  DayRemindersValidator;

export type Thought = DocumentMetadata &
  UserId &
  CreatedUpdated &
  ThoughtValidator;

export type Location = DocumentMetadata &
  CreatedUpdated &
  LocationValidator & { userId: string };

export enum LocationRelations {
  HappendAt,
  Remind,
}

export type LocationsRelations = DocumentMetadata &
  EdgeMetadata &
  CreatedUpdated & {
    type: LocationRelations;
    enter?: boolean;
    leave?: boolean;
  };

export type LocationReminder = DocumentMetadata &
  EdgeMetadata &
  CreatedUpdated & {
    type: LocationRelations.Remind;
    enter?: boolean;
    leave?: boolean;
  };

export enum UsageEventTypes {
  Created,
  Triggered,
  Reacted,
  CardFlipped,
  Used,
  Updated,
  Loaded,
  Viewed,
  Messaged,
  Phoned,
  OutsideLinkClicked,
  Clicked,
  LoggedIn,
  /**
   * E27 — the force-upgrade gate was SHOWN to this person (board slice
   * `E27 · RecordUsageEvent · upgrade-gate-shown`, founder 2026-08-29: "usage emit only"). The one
   * thing about the gate that leaves the device: the three observations behind it stay client-only
   * (registry `persist-gate-fact`), so this row is how a release campaign can see how many people a
   * floor is actually blocking. `extras` carries the board's `focusType`/`focus` (appVersion / the
   * running version) plus the floor and which source supplied it.
   */
  UpgradeGateShown,
  /**
   * ── M7 (E03 + E07, 2026-09-13) — SEVEN ADDITIVE MEMBERS, ONE CONTIGUOUS BLOCK ────────────────
   *
   * Founder ruling Q7 ("(a). Ok.") with the allocation applied as Q13's recommended default: one
   * block from the first free value, 14. The two boards had proposed incompatible schemes and one
   * of them COLLIDED with a live value — E03's chapters read "13 (SignedUp)" while
   * `UpgradeGateShown = 13` had already shipped with E27, and E07 separately proposed 31/32/33/37,
   * a seventeen-value gap in a plain implicit-ordinal enum where a gap can only be created by
   * writing the numbers out. Two epics landing in one milestone cannot carry two schemes.
   *
   * ADDITIVE ON THE WIRE and invisible to an old client: the SDL exposes usage only as `type: Int!`
   * (`packages/schema/src/graphql/be/*.gql`), so an added member changes no contract. `LoggedIn`
   * stays 12 and the PORTAL login keeps reusing it with `extras {surface: portal}` — the same act
   * on another surface, per the 2026-07-18 G1 (the collapsed-variant pattern), not a new type.
   *
   * ⚠ IMPLICIT ORDINALS: these are positional. Never insert above this line and never reorder —
   * a row already in `usage_log` carries the NUMBER, and moving a member silently relabels history.
   */
  /** E03 · sign-up completed. `focusType: feature`, `focus: signup`, no extras. */
  SignedUp,
  /** E03 · a password reset finished from the emailed link. `feature` / `passwordReset`. */
  PasswordResetCompleted,
  /**
   * E03 · consent and notification preferences saved. `feature` / `privacy`, NO extras.
   *
   * THIS REPLACES A SHIPPED ROW rather than adding one (founder Q14, recommendation (a) applied):
   * `PrivacySettings` save already appends `Updated` (5) with `_to` = the person's OWN `_id` and
   * `extras: JSON.stringify({privacySettings: values})`. That shape is wrong twice over for the E
   * era — the type says nothing about consent, and `extras` carries the domain payload the event
   * itself owns, which is exactly what EXTRAS-1 forbids.
   */
  PrivacyUpdated,
  /** E07 · a client connected to a therapist. `therapistClientRelation` / the relation id. */
  ConnectedToTherapist,
  /** E07 · a client invited their therapist. `invite` / the invite id. */
  InviteSent,
  /** E07 · a sharing permission was toggled. `therapistClientRelation` / the relation id. */
  SharingPermissionsChanged,
  /**
   * E07 · the LAST sharing permission went off — the churn signal (founder option (a),
   * 2026-08-24). It has no control of its own: disconnect is the derived state when the fourth
   * toggle goes off, so the emit fires from the confirm handler when the RESULTING set is
   * all-false. Distinct from a partial toggle, which is `SharingPermissionsChanged`.
   */
  SharingDisconnected,
  /**
   * E30 · the portal told a therapist that a newer build is being served — ordinal 21, the next
   * free value after M7's contiguous block (founder G1 2026-09-13).
   *
   * The portal analogue of `UpgradeGateShown` (13), and recorded for the same reason: the five E30
   * observations are `scope: client / sync: never` and never reach the log, so this row is the ONLY
   * trace a deploy's adoption leaves. It answers "how many of my therapists have been told about
   * this deploy?" — ONCE per tab session, on the notification's own open, so it counts PEOPLE WHO
   * SAW THE OFFER rather than offers made (registry `once-per-tab-session`, ruled as recommended).
   *
   * Its focus is the BUILD, not a document: `_to` is `portalBuild/<baked build id>`, which
   * `focusOf()` splits into `focusType: portalBuild` / `focus: <build id>` with no
   * `FEATURE_FLAGS_IDS` entry — the build id IS the domain noun (registry `A-usage-ordinal`).
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  PortalUpdatePromptShown,
  /**
   * E24 · the onboarding wizard's step 1 (Privacy & Terms) was completed — ordinal 22, the next
   * free value (the board says "ONBOARDING_STEP_COMPLETED (enum int at build)"; this is the build).
   * `focusType: onboardingStep`, `focus: privacy-terms`, no extras. Distinct from `PrivacyUpdated`
   * (16), which is the consent fact itself on either surface; this one counts wizard progress.
   */
  OnboardingStepCompleted,
  /**
   * E24 · a practice was created from the onboarding wizard's step 3 — ordinal 23 (the board's
   * "PRACTICE_CREATED (enum int at build)"). `focusType: practice`, `focus: the practice's E24
   * identity`, no extras. The profile-edited emit reuses `Created` (0) / `Updated` (5) with
   * `focusType: therapistProfile` — the board's example there is the kernel's own placeholder
   * (`3 (CardFlipped)` / `thoughtTest`), a board-round correction.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  PracticeCreated,
  /**
   * E04 · CLIENT_OPENED — the therapist opened a client (the Client Activity Monitor,
   * `pages/clients/show.tsx`) — ordinal 24 (the board's "34 · CLIENT_OPENED (new UsageEventType)"
   * was written before the M7 block allocation; the value is the next free ordinal, as every
   * member since has been). Emitted by the PORTAL through its usage provider — "usage events belong
   * where they happen" (founder convention, CC-129) — as `_to: users/<key>`, so the E-era split
   * reads `focusType: users`, `focus: <key>`; `extras {surface: portal}`.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  ClientOpened,
  /**
   * E08 · the paywall's three usage emits (board: RecordUsageEvent · paywall-viewed / purchase /
   * restore, "enum int at build"; built 2026-09-21) — the app's outbox, like every mobile emit:
   *   25 PaywallViewed — the paywall opened: `feature` / `paywall` (`featureFlags/23`, synthetic);
   *   26 SubscriptionPurchased — Adapty answered `success`: `subscriptions` / `premium`;
   *   27 PurchasesRestored — Adapty found a purchase to restore: `subscriptions` / `premium`.
   * A cancelled or pending purchase and a restore that found nothing (Adapty 1004) record nothing.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  PaywallViewed,
  SubscriptionPurchased,
  PurchasesRestored,
  /**
   * E32 · the person opened a What's New article from the card / banner / history (board:
   * "RecordUsageEvent · whats-new-article-opened", type "45" written as a placeholder before the
   * allocation; the value is the next free ordinal, 28, as every member since M7 has been) — the
   * marketing tail's one telemetry signal. `_to` is `featureAnnouncement/<announcementId>`, which
   * `focusOf()` splits into `focusType: featureAnnouncement` / `focus: <announcementId>` with no
   * `FEATURE_FLAGS_IDS` entry — the announcement id IS the domain noun (the `portalBuild`
   * precedent); `extras {surface}` says which surface, never the copy.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  FeatureAnnouncementArticleOpened,
  /**
   * E34 · the client opened one homework assignment's detail on the phone (board slice
   * "E34 · RecordUsageEvent · homework-opened", cc-homework "Homework — My homework (mobile, E34)";
   * the board's "29 (HomeworkOpened — allocated at build)" — and 29 IS the next free ordinal, so the
   * proposal and the allocation agree). Once per detail open; the practice board's last-activity
   * column and the RTM engagement evidence read it, no domain view is built from it (USAGE-2). `_to`
   * is `homework/<assignmentId>`, which `focusOf()` splits into `focusType: homework` / `focus: <id>`
   * with no `FEATURE_FLAGS_IDS` entry — the assignment IS the domain noun (the `portalBuild`
   * precedent); no extras. The first open's domain fact is `HomeworkAcknowledged` — this row counts
   * every open, the fact only the first.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  HomeworkOpened,
  /**
   * The person put an item in their OWN calendar — "Add to calendar" on a homework assignment
   * (E34 · RecordUsageEvent · homework-added-to-calendar, cc-homework "Homework — My homework (mobile,
   * E34)"; buildOrder 45). The board's "a new usage ordinal, allocated at build — shared by E34, E36,
   * E41, E42": ordinal 30, the next free one after HomeworkOpened's 29; the other three epics reuse it
   * with their own focus. `_to` is `homework/<assignmentId>` (`focusType homework`); `extras`
   * `{platform, recurrence}` — usage-scoped context only (a calendar entry is a SNAPSHOT: nothing about
   * it reaches the server, no calendar data, no person reference). What it counts is the HAND-OFF to
   * the system's calendar (the person can still cancel there) — build finding F-131.
   *
   * E41 · reused with `focusType: plannedActivity` (`_to` is `plannedActivity/<plannedActivityId>`); a saved monitored hour's
   * `Created` event carries `focusType: activityLog` (`_to` is `activityLog/<activityLogId>`). Both focus types are the domain
   * noun itself (the `homework` precedent): no `FEATURE_FLAGS_IDS` entry, no person reference.
   *
   * E45 · a saved sleep-diary night records `Created` with `focusType: sleepNight` (`_to` is `sleepNight/<YYYY-MM-DD>`; none for a skipped
   * night) — the domain noun itself, no `FEATURE_FLAGS_IDS` entry, no person reference, no health value.
   *
   * ⚠ IMPLICIT ORDINALS: positional. Never insert above this line and never reorder.
   */
  AddedToCalendar,
}

export enum StatsInterval {
  Week,
  Month,
  ThreeMonths,
  SixMonths,
  Year,
  AllTime,
}

export type UsageEventStats = {
  stats: { date: string; count: number }[];
};

export type UsageEventsRelation = DocumentMetadata &
  EdgeMetadata &
  Created & {
    type: UsageEventTypes;
  };

export type DateTimeReminder = DocumentMetadata &
  CreatedUpdated &
  DateTimeReminderValidator;

export enum TestType {
  phq9,
  gad7,
  // E35 — allocated at build (the instrument definitions are `@cc/domain` assessments/instruments.ts)
  dass21,
  pcl5,
  gds15,
  // E49 — the URICA (stages of change), "type 6, proposed" on the board; 5 stays unallocated
  urica = 6,
}
/**
 * N-5 · the built-in instrument ids, in catalogue order — DERIVED from `TestType`, so an instrument added there reaches every
 * portal list at once (take in session · hub filter · record external result · assign) and cannot be missed again. The
 * domain's `INSTRUMENT_TYPE_BY_ID` holds the same table; `model.test.ts` in the portal pins the two together.
 */
export type AssessmentInstrumentId = Exclude<keyof typeof TestType, number>;
export const ASSESSMENT_INSTRUMENT_IDS = Object.keys(TestType).filter((k) => Number.isNaN(Number(k))) as AssessmentInstrumentId[];
export type Test = DocumentMetadata &
  Created &
  TestValidator & {
    type: TestType;
    /** E35 — the erasure key: the client for their OWN take, null for a practice's record (migration 66 backfills). */
    owner?: string | null;
    /** E35 — the therapist who entered the take in session. */
    performer?: string | null;
    /** E35 — the practice whose record the take is (in session, or a practice-assigned instrument). */
    practiceId?: string | null;
  };

export enum InviteType {
  PERMANENT_LINK_CLIENT,
  CLIENT,
  THERAPIST,
  PRACTICE_USER,
  PRACTICE_ADMIN,
}

export type Practice = DocumentMetadata &
  CreatedUpdated &
  PracticeValidator & {
    saasSignedUser: string;
    practiceLocations?: {
      data: PracticeLocation[];
      count: number;
    };
    subscription?: {
      stripeCustomerId?: string;
      subscriptions?: {
        id: string;
        status: string;
        currentPeriodEnd: string;
        currentPeriodStart: string;
        canceledAt: string | null;
        cancelAtPeriodEnd: boolean;
        latestInvoiceId?: string | null;
        plan: {
          productId: string;
          priceId: string;
          quantity: number;
        };
      }[];
      quantity?: number;
      currentPeriodStart?: string;
      currentPeriodEnd?: string;
      cancelAtPeriodEnd?: boolean;
      status?: string;
      freeAppsUsed?: number;
      downgradeWarningSeenAt?: string | null;
      currentProductId?: string | null;
      currentPriceId?: string | null;
    } | null;
    trialDays?: number | null;
    trialEndDate?: string | null;
    trialStartedAt?: string | null;
  };

export type PracticeLocation = DocumentMetadata &
  CreatedUpdated &
  PracticeLocationValidator & {
    practiceId: string;
  };

export type Invite = DocumentMetadata & {
  token: string;
  createdAt: string;
  expiresAt: string;
  from: string;
  to?: string | null;
  type: InviteType;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  message?: string | null;
  allowEdit?: boolean | null;
  allowView?: boolean | null;
  allowAdminView?: boolean | null;
  allowAdminEdit?: boolean | null;
};

export enum SubscriptionType {
  ADAPTY,
  PROVIDER,
  MANUAL,
}

export type Subscription = DocumentMetadata &
  CreatedUpdated & {
    userId: string;
    type: SubscriptionType;
    practiceId?: string | null;
    lastSyncrhonizedAt?: string | null;
    name?: string | null;
    startsAt?: string | null;
    expiresAt?: string | null;
    activatedAt?: string | null;
    isActive?: boolean | null;
    isLifetime?: boolean | null;
    renewedAt?: string | null;
    unsubscribedAt?: string | null;
    vendorProductId?: string | null;
    willRenew?: boolean | null;
    clientsCount?: number | null;
    price?: number | null;
  };

type ProfileWithExtra = ProfileValidator &
  DocumentMetadata &
  CreatedUpdated &
  UserId & {
    practiceCity: string;
    practiceState: string;
    practiceCountry: string;
    practiceZip: string;

    lastLoadedAt?: string | null;
  };

export type Profile = Omit<ProfileWithExtra, 'firstName' | 'lastName'>;

export const STRIPE_PRODUCT_META_NAMES = {
  client_mobile_apps: 'client_mobile_apps',
  phone_support: 'phone_support',
  priority_support: 'priority_support',
};

export type Message = DocumentMetadata &
  Created & {
    from: string;
    to: string;
    message: string;
    readAt?: string | null;
    fromUser?: User | null;
  };

export enum AssignmentType {
  PRE_SESSION_SURVEY,
  POST_SESSION_SURVEY,
  TEST_PHQ9,
  TEST_GAD7,
}

export enum NotifcationType {
  EMAIL,
  MOBILE,
}
